import React, {FC, useState} from "react";
import {Button, Form, Input, InputNumber, Modal, Space, Tooltip} from "antd";
import {DeleteOutlined, EditOutlined, UndoOutlined} from "@ant-design/icons";
import moment, {Moment} from "moment";
import {LessonBookingRateEditInput} from "../../../generated/graphql";
import DateInput from "../../../views/DateInput/DateInput";

// Handänderung einer offenen Rate im neuen Preismodell (Wunsch 27.10.2026): Betrag, Fälligkeit und Notiz in einem kleinen Dialog,
// Löschen direkt; beides lässt sich bis zum Speichern zurücknehmen. Der Server rechnet den Plan mit der Änderung neu.

// rateId der Rate, die der Plan neu anlegt (Server: NEW_RATE_KEY)
export const NEW_RATE_ID = "new";

type RateEditProps = {
    amount: number;
    paymentDue: Date | string;
    note?: string | null;
    edit?: LessonBookingRateEditInput;
    deleted: boolean;
    onChange: (edit: LessonBookingRateEditInput | null) => void;
    rateId: string;
};

const RateEdit: FC<RateEditProps> = ({amount, paymentDue, note, edit, deleted, onChange, rateId}) => {
    const [open, setOpen] = useState(false);
    const [nextAmount, setNextAmount] = useState<number | null>(amount);
    const [nextDue, setNextDue] = useState<Moment | null>(moment(paymentDue));
    const [nextNote, setNextNote] = useState<string>(note ?? "");

    const openDialog = () => {
        setNextAmount(amount);
        setNextDue(moment(paymentDue));
        setNextNote(note ?? "");
        setOpen(true);
    };
    const apply = () => {
        if (nextAmount === null || nextAmount < 0.01 || !nextDue) return;
        // Nur geänderte Felder, zu einer früheren Handänderung dazu: ein nicht angefasster Betrag folgt weiter dem Plan, eine
        // frühere Notiz-Änderung bleibt (Review #1738). Die Notiz schreibt auch der Bankabgleich, deshalb nie ungefragt.
        const changes: LessonBookingRateEditInput = {
            ...(edit && !edit.deleted ? edit : {}),
            rateId,
            ...(Math.abs(nextAmount - amount) > 0.005 ? {amount: nextAmount} : {}),
            ...(!nextDue.isSame(moment(paymentDue), "day") ? {paymentDue: nextDue.clone().startOf("day").toDate()} : {}),
            ...(nextNote.trim() !== (note ?? "").trim() ? {note: nextNote} : {}),
        };

        if (Object.keys(changes).length > 1) onChange(changes);
        setOpen(false);
    };

    // Feste Breite: die Knöpfe stehen in jeder Zeile an derselben Stelle (Browser-Test 27.10.).
    if (deleted) {
        return (
            <Space size={0} style={{width: 48, justifyContent: "flex-end"}}>
                <Tooltip title="Löschen zurücknehmen">
                    <Button size="small" type="text" icon={<UndoOutlined />} onClick={() => onChange(null)} aria-label="Löschen zurücknehmen" />
                </Tooltip>
            </Space>
        );
    }

    return (
        <>
            <Space size={0} style={{width: 48, justifyContent: "flex-end"}}>
                <Tooltip title="Betrag, Fälligkeit oder Notiz ändern">
                    <Button size="small" type="text" icon={<EditOutlined />} onClick={openDialog} aria-label="Rate ändern" />
                </Tooltip>
                <Tooltip title="Rate löschen">
                    <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange({rateId, deleted: true})} aria-label="Rate löschen" />
                </Tooltip>
            </Space>
            <Modal
                title="Rate ändern"
                visible={open}
                onOk={apply}
                onCancel={() => setOpen(false)}
                footer={[
                    ...(edit && !edit.deleted
                        ? [
                              <Button
                                  key="reset"
                                  icon={<UndoOutlined />}
                                  onClick={() => {
                                      onChange(null);
                                      setOpen(false);
                                  }}
                                  style={{float: "left"}}
                              >
                                  {"Handänderung zurücknehmen"}
                              </Button>,
                          ]
                        : []),
                    <Button key="cancel" onClick={() => setOpen(false)}>
                        {"Abbrechen"}
                    </Button>,
                    <Button key="ok" type="primary" onClick={apply} disabled={nextAmount === null || nextAmount < 0.01 || !nextDue}>
                        {"Übernehmen"}
                    </Button>,
                ]}
                width={360}
                destroyOnClose
            >
                <Form layout="vertical">
                    <Form.Item label="Betrag" style={{marginBottom: 12}}>
                        <InputNumber
                            value={nextAmount ?? undefined}
                            onChange={(value) => setNextAmount(typeof value === "number" ? value : null)}
                            min={0.01}
                            max={100000}
                            precision={2}
                            decimalSeparator=","
                            addonAfter="€"
                            style={{width: "100%"}}
                        />
                    </Form.Item>
                    <Form.Item label="Fällig am" style={{marginBottom: 12}}>
                        <DateInput value={nextDue ?? undefined} onChange={(value) => setNextDue(value)} allowClear={false} style={{width: "100%"}} />
                    </Form.Item>
                    <Form.Item label="Notiz zum Posten" style={{marginBottom: 0}}>
                        <Input.TextArea rows={3} maxLength={2000} value={nextNote} onChange={(event) => setNextNote(event.target.value)} aria-label="Notiz zum Posten" />
                    </Form.Item>
                </Form>
                <div style={{marginTop: 12, color: "#888", fontSize: 12}}>{"Der Preis der Buchung ändert sich um die Differenz. Zum Entfernen die Rate löschen."}</div>
            </Modal>
        </>
    );
};

export default RateEdit;
